import BaseDrawer from '@/components/BaseDrawer'
import { useTranslation } from 'next-i18next'
import { FC, memo, ReactNode, useEffect, useState } from 'react'
import { FundraisingSelectItemStyled, FundraisingSelectStyled, FundraisingSelectWrapStyled } from './style'
import { BaseDataList, BaseOptionItem } from 'api-fundraising'
import { Flex } from '@mantine/core'
import { Box, Input } from '@gate/gui'
import BaseEmpty from '@/components/BaseEmpty'
import { stopEvent } from '@/utils/tools'

export interface FundraisingSelectProps {
  title: string
  data: BaseDataList<BaseOptionItem>
  value?: string
  onChange?: (value: string) => void
  isAll?: boolean
  miw?: number
  w?: number
  h?: number | string
  isMultiple?: boolean
  extra?: ReactNode
  isSearch?: boolean
  searchNode?: ReactNode
}

export const FundraisingSelectWrap: FC<
  FundraisingSelectProps['data'] &
    Pick<FundraisingSelectProps, 'value' | 'onChange' | 'isAll' | 'isMultiple' | 'extra' | 'h'>
> = ({ h, extra, list, isAll = true, isMultiple = true, value = ' ', onChange }) => {
  const { t } = useTranslation()

  return (
    <FundraisingSelectWrapStyled h={h} direction="column">
      {isAll && (
        <FundraisingSelectItemStyled data-focus={String(value === ' ')} onClick={() => void onChange?.(' ')}>
          <span className="text-1 size-14 font-500 focus-text">{t('P.All')}</span>
          {value === ' ' && (
            <svg className="iconpark-icon size-16 text-brand-1 focus-icon">
              <use href="#v31-16px-success"></use>
            </svg>
          )}
        </FundraisingSelectItemStyled>
      )}

      {list?.length ? (
        <>
          {list?.map(item => (
            <FundraisingSelectItemStyled
              key={item?.value}
              data-focus={
                isMultiple ? String(value && value?.includes(item?.value)) : 'key' + value === 'key' + item.value
              }
              onClick={e => {
                stopEvent(e)
                if (!isMultiple) {
                  onChange?.(item?.value)
                  return
                }
                if (value === ' ') {
                  onChange?.(item?.value)
                  return
                }

                const map = value?.split(',')?.filter(f => f?.length)

                if (map?.length && map?.includes(item?.value)) {
                  const newMap = map?.filter(f => f !== item?.value)
                  onChange?.(newMap.length ? newMap?.join(',') : ' ')
                } else {
                  onChange?.([...map, item?.value]?.join(','))
                }
              }}
            >
              <span className="text-1 size-14 font-500 focus-text">{item.label}</span>
              {(isMultiple ? value && value?.includes(item?.value) : 'key' + value === 'key' + item?.value) && (
                <svg className="iconpark-icon size-16 text-brand-1 focus-icon">
                  <use href="#v31-16px-success"></use>
                </svg>
              )}
            </FundraisingSelectItemStyled>
          ))}
        </>
      ) : (
        <Flex w="calc(100% - 4px)" h={'100%'} justify="center" align="center">
          <BaseEmpty type="search" size="xs" />
        </Flex>
      )}

      {extra}
    </FundraisingSelectWrapStyled>
  )
}

const FundraisingSelect: FC<FundraisingSelectProps> = ({
  title,
  data,
  miw = 163,
  w = 163,
  h = '238px',
  value,
  onChange,
  isMultiple,
  isSearch = true,
  extra,
  searchNode,
}) => {
  const { t } = useTranslation()
  const [search, setSarch] = useState<string>('')
  const [list, setList] = useState(data?.list ?? [])

  useEffect(() => {
    if (search?.length) {
      setList(data?.list?.filter(item => item?.label?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase())))
    } else {
      setList(data?.list)
    }
  }, [search, data?.list])

  return (
    <FundraisingSelectStyled>
      <BaseDrawer
        close
        customClose
        isScroll={false}
        w={w}
        maw={w}
        miw={miw}
        isButton={false}
        isRight={true}
        title={title}
        target={<span className="size-14 font-400 text-3">{title}</span>}
      >
        <Flex w="100%" direction="column">
          {searchNode ? (
            <>{searchNode}</>
          ) : (
            <>
              {isSearch && (
                <Box px={8} pb={8}>
                  <Input
                    w={'100%'}
                    icon={
                      <svg className="iconpark-icon size-14">
                        <use href="#v31-16px-line-sousuo"></use>
                      </svg>
                    }
                    placeholder={t('P.Search') + ''}
                    value={search}
                    onChange={(e: any) => {
                      setSarch(e.target.value)
                    }}
                    onClear={() => {
                      setSarch('')
                    }}
                  />
                </Box>
              )}
            </>
          )}

          <FundraisingSelectWrap
            h={h}
            isAll={Boolean(list?.length)}
            value={value}
            onChange={value => {
              onChange?.(value)
            }}
            {...data}
            list={list}
            isMultiple={isMultiple}
            extra={extra}
          />
        </Flex>
      </BaseDrawer>
    </FundraisingSelectStyled>
  )
}

export default memo(FundraisingSelect)
